import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useCreateEscalation,
  useEscalationContext,
} from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { useState } from "react";

interface UseInChatEscalationParams {
  messages: Message[];
  input: unknown;
  onSuccess: () => void;
}

export function useInChatEscalation({
  messages,
  input,
  onSuccess,
}: UseInChatEscalationParams) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const escalationContext = useEscalationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEscalated, setIsEscalated] = useState(false);

  function buildChatHistory() {
    return messages
      .filter((msg) => msg.role !== "tool")
      .map((msg) => {
        let content: string;
        if (typeof msg.content === "string") {
          content = msg.content;
        } else if (Array.isArray(msg.content)) {
          content = msg.content
            .filter(
              (part): part is { type: "text"; text: string } =>
                part.type === "text",
            )
            .map((part) => part.text)
            .join("\n");
        } else {
          content = "";
        }
        return { id: msg.id ?? undefined, role: msg.role, content };
      }) as {
      id?: string;
      role: "system" | "user" | "assistant";
      content: string;
    }[];
  }

  const createEscalation = useCreateEscalation({
    merchantRpcClient,
    buildChatHistory,
  });

  const escalate = useHandler(async () => {
    if (!escalationContext.triageThreadId) {
      setError("Unable to escalate: conversation thread not found");
      return;
    }

    if (isEscalated || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const toolInput = input as
        | { reason?: string; issueCategory?: string }
        | undefined;

      await createEscalation({
        triageThreadId: escalationContext.triageThreadId,
        escalationReason: toolInput?.reason,
        issueCategory: toolInput?.issueCategory,
      });

      setIsEscalated(true);
      onSuccess();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to connect to support";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  });

  return { escalate, isLoading, error, isEscalated };
}
